import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import Footer from './Footer';
import { MAX_IMAGES_PER_ORDER, cartOversizeMessage } from './orderLimits';

// REACT_APP_API_BASE_URL is baked in at build time by Vercel.
// Set it per environment (Production = 4534, Preview/Staging = 5d0c9).
// Fallback here is local dev only.
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4001/api';

// Shown when an order can't be placed. Same rules as the pricing messages in
// ProductPage: say what broke in plain words, say whether the order went
// through, give the customer something to try, and end with a human to email.
// No jargon — never "API", "server", or a status code.
//
// Two messages because the customer can only act on one of them. UNREACHABLE =
// the request never completed, so the problem sits between their device and us
// and switching networks is worth a try. SERVER_ERROR = we answered, just not
// correctly; their Wi-Fi is fine and telling them to change it sends them
// chasing the wrong thing.
//
// Both state plainly that nothing was submitted. That is the whole point: on
// 2026-08-26 the platform's habit of implying success left an order's fate
// ambiguous, and a customer who can't tell whether an order landed either
// double-orders or waits on one that never existed.
const ORDER_UNREACHABLE_MESSAGE =
  "We couldn't send your order — it never reached us, so nothing was submitted.\n\n" +
  'First, try again — this is usually temporary.\n\n' +
  "If that doesn't help, your internet connection may be blocking part of our " +
  'site. This is common on school, office, and guest Wi-Fi networks. Try again ' +
  "using your phone's data or a home connection.\n\n" +
  'Still stuck? Email us at support@presspeels.com.';

// A 500 can land after the order number was issued — the backend creates it
// well before the last step. "Nothing was submitted" would be a lie there, and
// a customer who believes it orders again. Name the number instead.
const orderPartialMessage = (orderNumber) =>
  'Something went wrong while we were saving your order, and we couldn\'t finish it.\n\n' +
  `Your order was given the number ${orderNumber} before the problem happened, ` +
  'so please do not submit it again — you could end up with the job run twice.\n\n' +
  'Email us at support@presspeels.com with that number so we can check what ' +
  'came through.';

const ORDER_SERVER_ERROR_MESSAGE =
  "We couldn't take your order just now, so nothing was submitted.\n\n" +
  'This one is on us, not your connection. Please wait a minute and try again.\n\n' +
  'If it keeps happening, email us at support@presspeels.com.';

const useResponsive = () => {
  const [screenSize, setScreenSize] = useState(() => {
    const width = typeof window !== 'undefined' ? window.innerWidth : 1024;
    return {
      isMobile: width < 768,
      isTablet: width >= 768 && width < 1024,
      isSmallMobile: width < 376,
      isStandardMobile: width >= 376 && width < 428,
    };
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setScreenSize({
        isMobile: width < 768,
        isTablet: width >= 768 && width < 1024,
        isSmallMobile: width < 376,
        isStandardMobile: width >= 376 && width < 428,
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return screenSize;
};

function calculateVolumeRate(totalSquareInches) {
  if (totalSquareInches <= 10) return 0.35;
  if (totalSquareInches <= 20) return 0.25;
  if (totalSquareInches <= 50) return 0.15;
  if (totalSquareInches <= 100) return 0.10;
  if (totalSquareInches <= 200) return 0.08;
  if (totalSquareInches <= 400) return 0.07;
  if (totalSquareInches <= 600) return 0.06;
  if (totalSquareInches <= 800) return 0.05;
  if (totalSquareInches <= 1000) return 0.04;
  if (totalSquareInches <= 1500) return 0.03;
  if (totalSquareInches <= 2500) return 0.025;
  return 0.02;
}

const inputStyle = {
  width: '100%',
  padding: '0.75rem 1rem',
  border: '1.5px solid #e5e7eb',
  borderRadius: '8px',
  fontSize: '0.95rem',
  color: '#111827',
  background: '#fff',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.2s',
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
};

const labelStyle = {
  display: 'block',
  fontSize: '0.875rem',
  fontWeight: '600',
  color: '#374151',
  marginBottom: '0.375rem',
};

const fieldGroupStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
};

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC',
];

export default function BizOrderForm({ cartItems = [], onBack, onOrderPlaced }) {
  const { isMobile, isTablet, isSmallMobile, isStandardMobile } = useResponsive();

  // Business info
  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [customNote, setCustomNote] = useState('');

  // Address
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [stateName, setStateName] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('US');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [focusedField, setFocusedField] = useState(null);
  const formRef = useRef(null);

  // Pricing calculations
  const totalSquareInches = cartItems.reduce((sum, item) => {
    const pw = item.customData?.printWidth || 0;
    const ph = item.customData?.printHeight || 0;
    return sum + pw * ph * item.quantity;
  }, 0);
  const rate = calculateVolumeRate(totalSquareInches);
  const totalPriceDollars = cartItems.reduce((sum, item) => sum + item.price / 100, 0);
  const totalQuantity = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!companyName.trim() || !contactName.trim() || !email.trim() || !phone.trim() || !poNumber.trim()) {
      setError('Please fill in all required business information fields (marked with *).');
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }

    if (!address1.trim() || !city.trim() || !stateName.trim() || !postalCode.trim() || !country.trim()) {
      setError('Please fill in all required address fields (marked with *).');
      return;
    }

    if (cartItems.length === 0) {
      setError('Your cart is empty. Please go back and add images.');
      return;
    }

    // Adding past the cap is blocked in the cart, but a cart can still arrive
    // here oversized — it persists in IndexedDB for 24 hours, so one built
    // before this limit existed outlives the deploy that introduced it. The
    // upload would be rejected wholesale after every file had been sent, so
    // say what to fix before spending the customer's bandwidth on it.
    if (cartItems.length > MAX_IMAGES_PER_ORDER) {
      setError(cartOversizeMessage(cartItems.length));
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('companyName', companyName.trim());
      formData.append('contactName', contactName.trim());
      formData.append('email', email.trim());
      formData.append('phone', phone.trim());
      formData.append('poNumber', poNumber.trim());
      formData.append('customNote', customNote.trim());
      formData.append('address1', address1.trim());
      formData.append('address2', address2.trim());
      formData.append('city', city.trim());
      formData.append('state', stateName.trim());
      formData.append('postalCode', postalCode.trim());
      formData.append('country', country.trim());
      formData.append('uploadCount', cartItems.length.toString());

      const uploadsData = cartItems.map((item, index) => ({
        fileIndex: index,
        dimensionSets: [{
          width: (item.customData?.printWidth || 0).toFixed(2),
          height: (item.customData?.printHeight || 0).toFixed(2),
          quantity: item.quantity,
        }],
        // Pass through the cart's server-generated spot TIFF reference so the
        // business handler can reuse it instead of regenerating. If the file
        // is no longer on Railway's ephemeral disk by the time the order
        // submits (container restart, redeploy), the backend self-heals by
        // regenerating from the PNG that's just been uploaded.
        spotTifTempPath: item.customData?.spotTifTempPath || null,
        whiteRemoved: !!item.customData?.whiteRemoved,
        blackRemoved: !!item.customData?.blackRemoved,
      }));
      formData.append('uploadsData', JSON.stringify(uploadsData));

      cartItems.forEach((item, index) => {
        formData.append(`file_${index}`, item.file, item.fileName);
      });

      const response = await fetch(`${API_BASE_URL}/business-order`, {
        method: 'POST',
        body: formData,
      });

      // A backend that is down often answers with an HTML error page from the
      // host's proxy rather than JSON, so parsing is guarded. Letting it throw
      // would drop us into the catch below, which blames the customer's
      // connection for what is actually our own outage.
      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (response.ok && data?.success) {
        // Carry the per-step outcomes to the success page so it can tell the
        // customer what actually happened. Anything that failed is passed
        // explicitly; a missing flag means that step was fine.
        const params = new URLSearchParams({
          order: data.orderNumber,
          total: totalPriceDollars.toFixed(2),
        });
        if (data.receipts?.customer !== 'sent') params.set('receipt', 'failed');
        if (data.artworkStored === false) params.set('artwork', 'failed');
        // The order exists now, so the saved cart has to go before we leave —
        // otherwise it restores on the next visit and invites a resubmit.
        // Only on success: a failed order keeps its cart so they can retry.
        if (onOrderPlaced) await onOrderPlaced();
        window.location.href = `/biz-success?${params.toString()}`;
      } else if (response.status >= 400 && response.status < 500 && data?.error) {
        // A 4xx is something the customer can fix, and the backend names the
        // exact field. That beats any generic wording we could substitute.
        setError(data.error);
      } else if (data?.orderNumber) {
        // We broke partway through, after the order was created.
        setError(orderPartialMessage(data.orderNumber));
      } else {
        // A 5xx with nothing created, or an answer we couldn't read. The
        // backend's own 500 text ("Failed to process business order") gives the
        // customer nothing to act on, so it is deliberately not shown.
        setError(ORDER_SERVER_ERROR_MESSAGE);
      }
    } catch (err) {
      console.error('Business order submission error:', err);
      setError(ORDER_UNREACHABLE_MESSAGE);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFocusStyle = (fieldName) => focusedField === fieldName
    ? { ...inputStyle, borderColor: '#635bff', boxShadow: '0 0 0 3px rgba(99, 91, 255, 0.12)' }
    : inputStyle;

  const sectionCardStyle = {
    background: 'white',
    borderRadius: '16px',
    padding: isMobile ? '1.25rem' : '1.75rem',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    border: '1px solid #f3f4f6',
  };

  const containerWidth = isSmallMobile ? '100%' : isMobile ? '100%' : isTablet ? '720px' : '1000px';

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f3f4f6',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header */}
      <header style={{
        background: 'white',
        textAlign: 'center',
        padding: isSmallMobile ? '0.75rem 0.5rem' : isStandardMobile ? '1rem 0.75rem' : isMobile ? '1.125rem 0.75rem' : isTablet ? '1.25rem 1rem' : '1.375rem 0.5rem',
        boxShadow: '0 2px 20px rgba(0,0,0,0.1)',
      }}>
        <img
          src="/press-peels-logo-02.svg"
          alt="Press Peels"
          style={{
            maxWidth: '100%',
            height: 'auto',
            width: isSmallMobile ? '200px' : isStandardMobile ? '280px' : isMobile ? '320px' : isTablet ? '350px' : '476px',
            maxHeight: isSmallMobile ? '76px' : isStandardMobile ? '106px' : isMobile ? '122px' : isTablet ? '118px' : '136px',
            objectFit: 'contain',
          }}
        />
      </header>

      {/* Breadcrumb bar */}
      <div style={{
        background: '#f9fafb',
        borderBottom: '1px solid #e5e7eb',
        padding: '0.75rem 1rem',
      }}>
        <div style={{ maxWidth: containerWidth, margin: '0 auto', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
          <button
            onClick={onBack}
            style={{
              background: 'none',
              border: 'none',
              color: '#635bff',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: '500',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 5l-7 7 7 7" />
            </svg>
            Back to Cart
          </button>
          <span style={{ color: '#9ca3af' }}>/</span>
          <span style={{ color: '#374151', fontWeight: '600' }}>Business Order Form</span>
        </div>
      </div>

      {/* Main content */}
      <div style={{
        flex: 1,
        padding: isMobile ? '1rem' : '2rem 1rem',
        maxWidth: containerWidth,
        margin: '0 auto',
        width: '100%',
        boxSizing: 'border-box',
      }}>

        {/* Page title */}
        <div style={{
          marginBottom: isMobile ? '1.25rem' : '1.75rem',
          background: 'linear-gradient(135deg, #635bff 0%, #5a52e8 100%)',
          borderRadius: '14px',
          padding: isMobile ? '1.25rem 1.25rem' : '1.5rem 2rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
        }}>
          <div style={{
            width: isMobile ? 40 : 48,
            height: isMobile ? 40 : 48,
            flexShrink: 0,
            background: 'rgba(255,255,255,0.18)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <svg width={isMobile ? 22 : 26} height={isMobile ? 22 : 26} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
            </svg>
          </div>
          <div>
            <h1 style={{
              fontSize: isMobile ? '1.2rem' : '1.5rem',
              fontWeight: '700',
              color: 'white',
              margin: '0 0 0.25rem 0',
            }}>
              Business Order
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.82)', margin: 0, fontSize: isMobile ? '0.82rem' : '0.9rem' }}>
              Review your order and fill in your business details to submit.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} ref={formRef} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

          {/* ── Order Summary ── */}
          <div style={sectionCardStyle}>
            <h2 style={{ fontSize: '1.05rem', fontWeight: '700', color: '#111827', margin: '0 0 1.25rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ width: 24, height: 24, background: 'linear-gradient(135deg, #635bff, #5a52e8)', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.75rem', fontWeight: '700' }}>1</span>
              Order Summary
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
              {cartItems.map((item, index) => {
                const pw = item.customData?.printWidth || 0;
                const ph = item.customData?.printHeight || 0;
                const sqIn = pw * ph * item.quantity;
                const itemPrice = item.price / 100;
                return (
                  <div key={item.cartItemId || index} style={{
                    display: 'flex',
                    gap: '0.875rem',
                    alignItems: 'flex-start',
                    padding: '0.875rem',
                    background: '#f9fafb',
                    borderRadius: '10px',
                    border: '1px solid #e5e7eb',
                  }}>
                    {/* Image preview */}
                    <div style={{
                      width: isMobile ? 52 : 64,
                      height: isMobile ? 52 : 64,
                      flexShrink: 0,
                      overflow: 'hidden',
                      background: 'repeating-conic-gradient(#e5e7eb 0% 25%, #f9fafb 0% 50%) 0 0 / 12px 12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid #e5e7eb',
                    }}>
                      <img
                        src={item.imagePreview}
                        alt={item.fileName}
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      />
                    </div>

                    {/* Item details */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#111827', marginBottom: '0.25rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.fileName}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#6b7280', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        <span>{pw.toFixed(2)}" × {ph.toFixed(2)}"</span>
                        <span style={{ color: '#d1d5db' }}>·</span>
                        <span>Qty: {item.quantity}</span>
                        <span style={{ color: '#d1d5db' }}>·</span>
                        <span>{sqIn.toFixed(1)} sq in</span>
                      </div>
                    </div>

                    {/* Price */}
                    <div style={{ flexShrink: 0, textAlign: 'right' }}>
                      <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#111827' }}>
                        ${itemPrice.toFixed(2)}
                      </div>
                      {item.quantity > 1 && (
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                          ${(itemPrice / item.quantity).toFixed(2)} ea
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Totals */}
            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.375rem' }}>
                <span>{totalQuantity} print{totalQuantity !== 1 ? 's' : ''} · {totalSquareInches.toFixed(1)} total sq in</span>
                <span>Rate: ${rate.toFixed(2)}/sq in</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '1rem', fontWeight: '700', color: '#111827' }}>Estimated Total</span>
                <span style={{ fontSize: '1.25rem', fontWeight: '700', color: '#111827' }}>${totalPriceDollars.toFixed(2)}</span>
              </div>
              <p style={{ fontSize: '0.775rem', color: '#9ca3af', margin: '0.5rem 0 0 0' }}>
                Final pricing may vary. Your team will confirm before production.
              </p>
            </div>
          </div>

          {/* ── Business Information ── */}
          <div style={sectionCardStyle}>
            <h2 style={{ fontSize: '1.05rem', fontWeight: '700', color: '#111827', margin: '0 0 1.25rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ width: 24, height: 24, background: 'linear-gradient(135deg, #635bff, #5a52e8)', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.75rem', fontWeight: '700' }}>2</span>
              Business Information
            </h2>

            <div style={fieldGroupStyle}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={labelStyle}>Company Name <span style={{ color: '#ef4444' }}>*</span></label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={e => setCompanyName(e.target.value)}
                    onFocus={() => setFocusedField('companyName')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="Acme Corporation"
                    style={getFocusStyle('companyName')}
                    required
                  />
                </div>
                <div>
                  <label style={labelStyle}>Contact Name <span style={{ color: '#ef4444' }}>*</span></label>
                  <input
                    type="text"
                    value={contactName}
                    onChange={e => setContactName(e.target.value)}
                    onFocus={() => setFocusedField('contactName')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="Jane Smith"
                    style={getFocusStyle('contactName')}
                    required
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={labelStyle}>Email Address <span style={{ color: '#ef4444' }}>*</span></label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="jane@acme.com"
                    style={getFocusStyle('email')}
                    required
                  />
                </div>
                <div>
                  <label style={labelStyle}>Phone Number <span style={{ color: '#ef4444' }}>*</span></label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    onFocus={() => setFocusedField('phone')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="(555) 000-0000"
                    style={getFocusStyle('phone')}
                    required
                  />
                </div>
              </div>

              <div>
                <label style={labelStyle}>PO Number <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  type="text"
                  value={poNumber}
                  onChange={e => setPoNumber(e.target.value)}
                  onFocus={() => setFocusedField('poNumber')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="PO-2026-001"
                  style={getFocusStyle('poNumber')}
                  required
                />
              </div>

              <div>
                <label style={labelStyle}>
                  Notes / Special Instructions
                  <span style={{ fontWeight: '400', color: '#9ca3af', marginLeft: '0.375rem' }}>(optional, {100 - customNote.length} chars remaining)</span>
                </label>
                <textarea
                  value={customNote}
                  onChange={e => setCustomNote(e.target.value.slice(0, 100))}
                  onFocus={() => setFocusedField('customNote')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="Any special requirements, rush orders, etc."
                  maxLength={100}
                  rows={3}
                  style={{
                    ...getFocusStyle('customNote'),
                    resize: 'vertical',
                    minHeight: '80px',
                    lineHeight: '1.5',
                  }}
                />
              </div>
            </div>
          </div>

          {/* ── Shipping Address ── */}
          <div style={sectionCardStyle}>
            <h2 style={{ fontSize: '1.05rem', fontWeight: '700', color: '#111827', margin: '0 0 1.25rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ width: 24, height: 24, background: 'linear-gradient(135deg, #635bff, #5a52e8)', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.75rem', fontWeight: '700' }}>3</span>
              Shipping Address
            </h2>

            <div style={fieldGroupStyle}>
              <div>
                <label style={labelStyle}>Address Line 1 <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  type="text"
                  value={address1}
                  onChange={e => setAddress1(e.target.value)}
                  onFocus={() => setFocusedField('address1')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="123 Main Street"
                  style={getFocusStyle('address1')}
                  required
                />
              </div>

              <div>
                <label style={labelStyle}>Address Line 2 <span style={{ fontWeight: '400', color: '#9ca3af' }}>(optional)</span></label>
                <input
                  type="text"
                  value={address2}
                  onChange={e => setAddress2(e.target.value)}
                  onFocus={() => setFocusedField('address2')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="Suite 100, Floor 3, etc."
                  style={getFocusStyle('address2')}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '2fr 1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={labelStyle}>City <span style={{ color: '#ef4444' }}>*</span></label>
                  <input
                    type="text"
                    value={city}
                    onChange={e => setCity(e.target.value)}
                    onFocus={() => setFocusedField('city')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="Los Angeles"
                    style={getFocusStyle('city')}
                    required
                  />
                </div>
                <div>
                  <label style={labelStyle}>State <span style={{ color: '#ef4444' }}>*</span></label>
                  <select
                    value={stateName}
                    onChange={e => setStateName(e.target.value)}
                    onFocus={() => setFocusedField('state')}
                    onBlur={() => setFocusedField(null)}
                    style={{
                      ...getFocusStyle('state'),
                      cursor: 'pointer',
                      appearance: 'none',
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2.5'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 0.75rem center',
                      paddingRight: '2.25rem',
                    }}
                    required
                  >
                    <option value="">State</option>
                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>ZIP Code <span style={{ color: '#ef4444' }}>*</span></label>
                  <input
                    type="text"
                    value={postalCode}
                    onChange={e => setPostalCode(e.target.value)}
                    onFocus={() => setFocusedField('postalCode')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="90001"
                    style={getFocusStyle('postalCode')}
                    required
                  />
                </div>
              </div>

              <div style={{ maxWidth: isMobile ? '100%' : '280px' }}>
                <label style={labelStyle}>Country <span style={{ color: '#ef4444' }}>*</span></label>
                <select
                  value={country}
                  onChange={e => setCountry(e.target.value)}
                  onFocus={() => setFocusedField('country')}
                  onBlur={() => setFocusedField(null)}
                  style={{
                    ...getFocusStyle('country'),
                    cursor: 'pointer',
                    appearance: 'none',
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2.5'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 0.75rem center',
                    paddingRight: '2.25rem',
                  }}
                  required
                >
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                  <option value="MX">Mexico</option>
                </select>
              </div>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#dc2626',
              padding: '1rem',
              borderRadius: '10px',
              fontSize: '0.9rem',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.5rem',
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '0.1rem' }}>
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              {/* pre-line keeps the blank lines between paragraphs. Without it
                  the guidance collapses into one dense block that customers
                  skim past — which defeats the point of writing steps out. */}
              <span style={{ whiteSpace: 'pre-line' }}>{error}</span>
            </div>
          )}

          {/* Submit buttons */}
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '0.875rem', paddingBottom: '1rem' }}>
            <button
              type="button"
              onClick={onBack}
              style={{
                flex: isMobile ? 'unset' : '0 0 auto',
                padding: '1rem 1.75rem',
                background: 'white',
                color: '#374151',
                border: '1.5px solid #d1d5db',
                borderRadius: '10px',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                minHeight: '48px',
                transition: 'all 0.15s',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 5l-7 7 7 7" />
              </svg>
              Back to Cart
            </button>

            <button
              type="submit"
              disabled={isSubmitting || cartItems.length === 0}
              style={{
                flex: 1,
                padding: '1rem 2rem',
                background: isSubmitting || cartItems.length === 0
                  ? '#9ca3af'
                  : 'linear-gradient(135deg, #635bff 0%, #5a52e8 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: isMobile ? '1rem' : '1.05rem',
                fontWeight: '700',
                cursor: isSubmitting || cartItems.length === 0 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                minHeight: '52px',
                boxShadow: isSubmitting || cartItems.length === 0 ? 'none' : '0 4px 15px rgba(99, 91, 255, 0.3)',
                transition: 'all 0.2s',
                opacity: isSubmitting || cartItems.length === 0 ? 0.7 : 1,
              }}
            >
              {isSubmitting ? (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 1s linear infinite' }}>
                    <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                    <path d="M12 2a10 10 0 0 1 10 10" />
                  </svg>
                  <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                  Submitting Order...
                </>
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 2L11 13M22 2L15 22l-4-9-9-4 19-7z" />
                  </svg>
                  Submit Business Order
                </>
              )}
            </button>
          </div>

        </form>
      </div>

      <Footer />
    </div>
  );
}
